import { doc, getDoc } from 'firebase/firestore';
import { db } from '@config/firebase';
import { StatsData, JourneyFunnelEntry, FirebaseStatsData } from '../types/introStats.types';

class IntroStatsService {
  async fetchIntroStats(tenantId: string): Promise<StatsData> {
    try {
      // Read stats from the tenant document field, not a subcollection
      const tenantRef = doc(db, 'tenants', tenantId);
      const tenantDoc = await getDoc(tenantRef);

      if (!tenantDoc.exists()) {
        console.warn('Tenant document not found, returning default values');
        return this.getDefaultStatsData();
      }

      const tenantData = tenantDoc.data();
      const statsData = tenantData?.stats as FirebaseStatsData | undefined;

      if (!statsData) {
        console.warn('No stats field found on tenant document, returning default values');
        return this.getDefaultStatsData();
      }

      return this.transformToStatsData(statsData);

    } catch (error) {
      console.error('Error fetching intro stats:', error);
      console.error('Error details:', error);
      throw error;
    }
  }

  private getDefaultStatsData(): StatsData {
    return {
      totalIntros: 0,
      successRate: 100,
      pendingIntros: 0,
      averageResponseTime: '< 1 day',
      npsScore: 100,
      npsResponses: 0,
      promoterPercentage: 100,
      detractorPercentage: 0,
      journeyFunnel: [],
      totalIntrosLastMonth: 0
    };
  }

  private transformToStatsData(firebaseData: Partial<FirebaseStatsData>): StatsData {
    const nps = firebaseData.netPromoterScore || {
      detractorsResponses: 0,
      npsScore: 100,
      passivesResponses: 0,
      promotersResponses: 0,
      totalResponses: 0
    };

    const promoterPercentage = nps.totalResponses > 0
      ? Math.round((nps.promotersResponses / nps.totalResponses) * 100)
      : 100;
    const detractorPercentage = nps.totalResponses > 0
      ? Math.round((nps.detractorsResponses / nps.totalResponses) * 100)
      : 0;

    return {
      totalIntros: firebaseData.totalIntroductions ?? 0,
      successRate: firebaseData.successRate ?? 100,
      pendingIntros: firebaseData.pendingIntroductions ?? 0,
      averageResponseTime: firebaseData.averageResponseTime
        ? `${firebaseData.averageResponseTime} days`
        : '< 1 day',
      npsScore: nps.npsScore ?? 100,
      npsResponses: nps.totalResponses ?? 0,
      promoterPercentage,
      detractorPercentage,
      journeyFunnel: firebaseData.journeyFunnel ?? [],
      totalIntrosLastMonth: firebaseData.totalIntroductionsLastMonth ?? 0
    };
  }

  transformJourneyFunnelForChart(journeyFunnel?: JourneyFunnelEntry[]) {
    if (!journeyFunnel || journeyFunnel.length === 0) {
      return {
        chartData: [
          {
            name: "FEEDBACK COLLECTED",
            data: []
          },
          {
            name: "CONNECTED",
            data: []
          },
          {
            name: "INTRODUCED",
            data: []
          }
        ],
        categories: []
      };
    }

    const sorted = [...journeyFunnel].sort((a, b) => {
      const dateA = a.month?.toDate ? a.month.toDate() : new Date(a.month);
      const dateB = b.month?.toDate ? b.month.toDate() : new Date(b.month);
      return dateA.getTime() - dateB.getTime();
    });

    const feedbackData = sorted.map(entry => entry.feedbackCollected ?? 0);
    const connectedData = sorted.map(entry => entry.connected ?? 0);
    const introducedData = sorted.map(entry => entry.introduced ?? 0);

    const monthLabels = sorted.map(entry => {
      const date = entry.month?.toDate ? entry.month.toDate() : new Date(entry.month);
      return date.toLocaleString('en-US', { month: 'short' });
    });

    return {
      chartData: [
        {
          name: "FEEDBACK COLLECTED",
          data: feedbackData
        },
        {
          name: "CONNECTED",
          data: connectedData
        },
        {
          name: "INTRODUCED",
          data: introducedData
        }
      ],
      categories: monthLabels
    };
  }
}

export const introStatsService = new IntroStatsService();